'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import Link from '@/components/public/i18n/LocalizedLink';
import { UNIVERSES as SOURCE_UNIVERSES, ELINA_MISSIONS as SOURCE_ELINA_MISSIONS, type Universe } from '@/lib/public/universes';
import Testimonials from '../testimonials/Testimonials';
import PublicShell from './PublicShell';
import UniverseVisual from './UniverseVisual';
import ProjectConfigurator from './ProjectConfigurator';
import { Arrow, UniverseIcon } from './Icons';
import RNodeDemo from './RNodeDemo';
import ElinaAccess from '../elina/ElinaAccess';
import RNodeCases from './RNodeCases';
import EcosystemContext from './EcosystemContext';
import { RNodeFocusEntry } from './RNodeFocus';
import { RNODE_CASES_URL } from '@/lib/public/rnode-cases';
export default function UniversePage({
  universe: sourceUniverse,
  intakeEnabled,
}: {
  universe: Universe;
  intakeEnabled: boolean;
}) {
  const { t, href, content, locale } = useI18n();
  const u = content(sourceUniverse);
  const UNIVERSES = content(SOURCE_UNIVERSES);
  const ELINA_MISSIONS = content(SOURCE_ELINA_MISSIONS);

  return (
    <PublicShell active={u.id}>
      <main id="contenu">
        <section className={`u-hero u-hero-${u.id}`} aria-labelledby="universe-title">
          <div className="u-hero-copy">
            <Link className="u-back" href={href("/")}>
              {t("← Accueil RDV-Partners")}</Link>
            <div className="u-universe-label">
              <UniverseIcon id={u.id} />
              <span>
                {u.name}
                <small>{t("by RDV-Partners")}</small>
              </span>
            </div>
            <p className="u-eyebrow">{u.descriptor}</p>
            <h1 id="universe-title">
              {u.headline}
              <br />
              <span>{u.highlight}</span>
            </h1>
            <p className="u-hero-description">{u.description}</p>
            <p className="u-hero-promise">{t("Vous donnez le cap. Nous concevons et déployons.")}</p>
            <div className="u-hero-cta-group">
              <a href={href("#configurateur")} className="u-button">
                {t("Composer mon projet ")}{u.name} <Arrow />
              </a>
              {u.id === 'r-node' && (
                <a href={href(`${RNODE_CASES_URL}/#metiers`)} className="u-button u-button-secondary">
                  {t("Explorer les cas pratiques ")}<Arrow diagonal />
                </a>
              )}
              {u.id === 'rise' && (
                <Link href={href("/rise/decouvrir")} className="u-button u-button-secondary">
                  {t("En savoir plus ")}<Arrow diagonal />
                </Link>
              )}
              {u.id === 'sx-business' && (
                <Link href={href("/sx-business/decouvrir")} className="u-button u-button-secondary">
                  {t("En savoir plus ")}<Arrow diagonal />
                </Link>
              )}
              {u.id === 'elina' && (
                <Link href={href("/elina/decouvrir?contexte=elina#echanger")} className="u-button u-button-secondary">
                  {t("Découvrir Élina en échangeant avec elle ")}<Arrow diagonal />
                </Link>
              )}
              {u.id === 'r-node' && <RNodeDemo inline />}
            </div>
            <p className="u-microcopy">{t("À votre rythme · Sans création de compte")}</p>
          </div>
          <UniverseVisual id={u.id} />
          <div className="u-hero-index">
            <span>{u.number} / 04</span>
            <a href={href("#comprendre")}>
              {t("Explorer l’univers ")}<span aria-hidden="true">↓</span>
            </a>
          </div>
        </section>
        <EcosystemContext universe={u.id} />
        <section id="comprendre" className="u-clarity u-wrap" aria-labelledby="clarity-title">
          <div className="u-section-heading">
            <p className="u-eyebrow">{t("01 — Comprendre ")}{u.name}</p>
            <h2 id="clarity-title">{u.clarity}</h2>
            <p>
              {t("Nous composons le périmètre utile à votre organisation, puis prenons en charge sa mise en œuvre.")}</p>
          </div>
          {u.id === 'elina' && (
            <div><h3>{t('Autres usages selon votre projet')}</h3><ul className="u-mission-list" aria-label={t('Autres usages selon votre projet')}>
              {ELINA_MISSIONS.filter(m => m !== t('Copilote')).map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul></div>
          )}
          <div className="u-outcomes">
            {u.outcomes.map((o, i) => (
              <article key={o.title}>
                <span className="u-outcome-number">0{i + 1}</span>
                <h3>{o.title}</h3>
                <p>{o.detail}</p>
              </article>
            ))}
          </div>
          {u.id === 'rise' && <div className="eco-rise-forms"><article><h3>{t('Portail public')}</h3><p>{t('Comprendre votre activité, trouver le bon service et engager une demande.')}</p></article><article><h3>{t('Site interactif')}</h3><p>{t('Préciser un besoin et suivre un parcours personnalisé vers une action utile.')}</p></article><article><h3>{t('Showroom partenaires')}</h3><p>{t('Retrouver une offre adaptée, échanger avec vos équipes et passer commande.')}</p></article></div>}
          {u.id === 'sx-business' && <Testimonials ids={['arianespace']} />}
          {u.id === 'r-node' && <><RNodeFocusEntry /><RNodeCases /></>}
          <ElinaAccess variant="inline" contextId={u.id} />
          <div className="u-complements">
            <div>
              <p className="u-eyebrow">{t("Votre projet peut aller plus loin")}</p>
              <p>
                {t('Retrouvez les contributions de chaque solution.')}<br />
                {t("Vos choix restent réunis.")}</p>
            </div>
            <div className="u-complement-links">
              {UNIVERSES.filter((x) => x.id !== u.id).map((x) => (
                <Link key={x.id} href={href(`/${x.id}`)}>
                  <UniverseIcon id={x.id} />
                  <span>
                    <strong>{x.name}</strong>
                    <small>{x.verb}</small>
                  </span>
                  <Arrow diagonal />
                </Link>
              ))}
            </div>
          </div>
        </section>
        <ProjectConfigurator universe={u} intakeEnabled={intakeEnabled} />
      </main>
    </PublicShell>
  );
}





